import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import httpRequest from "src/helpers/httpRequest";
import Slider from "react-slick";
import PieChartGraph from "src/Components/AdminComponents/Graphs/PieChartGraph";
import iconBiomarker from "src/assets/images/admin-dashboard/biomarker-icon.png";
import iconCategory from "src/assets/images/admin-dashboard/category-icon.png";
import iconOrder from "src/assets/images/admin-dashboard/purchase-order.png";
import iconRevenue from "src/assets/images/admin-dashboard/revenue-icon.png";
import { CircularProgress } from "@mui/material";
import ProgressGraph from "src/Components/AdminComponents/Graphs/ProgressGraph";
import SelectDropdown from "src/Components/GlobalComponents/SelectDropdown";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import { allMonths, allYears, CURRENCY } from "src/lib/constans";

interface ICategory {
  categoriesWithCounts: { mainCategoryName: string; productCount: number }[];
  totalProducts: number;
}
interface IBrand {
  brandId: string;
  brandName: string;
  totalCount: number;
}
interface IData {
  count: number;
  _id: { year: number; month: number };
  totalAmount: number;
}

type IStatsKeys =
  | "brands"
  | "mainCategories"
  | "subCategories"
  | "childCategories"
  | "products"
  | "totalOrder"
  | "totalRevenue";

const POverView: React.FC = () => {
  const [categoryLoader, setCategoryLoader] = useState<boolean>(false);
  const [pharmaCategories, setPharmaCategories] = useState<ICategory>({
    categoriesWithCounts: [],
    totalProducts: 0,
  });
  const fetchCategoriesCount = async () => {
    setCategoryLoader(true);
    const { res, err } = await httpRequest<ICategory>({
      path: "/pharma-inventory/get-category-count",
    });
    if (res) {
      setPharmaCategories(res);
    } else {
      toast(err, { type: "error" });
    }
    setCategoryLoader(false);
  };

  const [statsLoader, setStatsLoader] = useState<boolean>(false);
  const [statsData, setStatsData] = useState<
    { label: string; key: IStatsKeys; count: number; images: string }[]
  >([
    { label: "Total Brands", key: "brands", count: 0, images: iconBiomarker },
    {
      label: "Main Categories",
      key: "mainCategories",
      count: 0,
      images: iconCategory,
    },
    {
      label: "Sub Categories",
      key: "subCategories",
      count: 0,
      images: iconCategory,
    },
    {
      label: "Child Categories",
      key: "childCategories",
      count: 0,
      images: iconCategory,
    },
    { label: "Total Products", key: "products", count: 0, images: iconOrder },
    { label: "Total Orders", key: "totalOrder", count: 0, images: iconOrder },
    {
      label: "Total Revenue",
      key: "totalRevenue",
      count: 0,
      images: iconRevenue,
    },
  ]);
  const fetchStats = async () => {
    setStatsLoader(true);
    const { res, err } = await httpRequest<{
      data: { [keys in IStatsKeys]: number };
    }>({
      path: "/main-category/get-dashboard-data",
    });
    if (res) {
      const updatedStatsData = statsData.map((item) => {
        return {
          ...item,
          count: res.data[item.key],
        };
      });
      setStatsData(updatedStatsData);
    } else {
      toast(err, { type: "error" });
    }
    setStatsLoader(false);
  };

  const [brandLoader, setBrandLoader] = useState<boolean>(false);
  const [brandData, setBrandData] = useState<IBrand[]>([]);
  const fetchTopSellingBrands = async () => {
    setBrandLoader(true);
    const { res, err } = await httpRequest<{ data: IBrand[] }>({
      path: "/pharma-inventory/top/sellingBrands",
    });
    if (res) {
      setBrandData(res?.data || []);
    } else {
      toast(err, { type: "error" });
    }
    setBrandLoader(false);
  };

  const [salesLoader, setSalesLoader] = useState<boolean>(false);
  const [saleData, setSaleData] = useState<{
    category: "monthly" | "yearly";
    totalCount: number;
    monthlyData: IData[];
    yearlyData: IData[];
    data: { name: string; count: number }[];
  }>({
    category: "monthly",
    totalCount: 0,
    monthlyData: [],
    yearlyData: [],
    data: [],
  });
  const fetchSaleRecord = async () => {
    setSalesLoader(true);
    const { res, err } = await httpRequest<
      {
        totalCount: { count: number }[];
        monthlyData: IData[];
        yearlyData: IData[];
      }[]
    >({ path: "/pharma-inventory/graph/sales" });
    if (res) {
      setSaleData((prev) => ({
        ...prev,
        totalCount: res[0]?.totalCount[0]?.count || 0,
        monthlyData: res[0]?.monthlyData,
        yearlyData: res[0]?.yearlyData,
      }));
    } else {
      toast(err, { type: "error" });
    }
    setSalesLoader(false);
  };

  const [revenueLoader, setRevenueLoader] = useState<boolean>(false);
  const [revenueData, setRevenueData] = useState<{
    category: "monthly" | "yearly";
    totalCount: number;
    monthlyData: IData[];
    yearlyData: IData[];
    data: { name: string; count: number }[];
  }>({
    category: "monthly",
    totalCount: 0,
    monthlyData: [],
    yearlyData: [],
    data: [],
  });
  const fetchRevenueRecord = async () => {
    setRevenueLoader(true);
    const { res, err } = await httpRequest<
      {
        totalCount: { count: number }[];
        monthlyData: IData[];
        yearlyData: IData[];
      }[]
    >({ path: "/pharma-inventory/graph/revenue" });
    if (res) {
      setRevenueData((prev) => ({
        ...prev,
        totalCount: res[0]?.totalCount[0]?.count || 0,
        monthlyData: res[0]?.monthlyData,
        yearlyData: res[0]?.yearlyData,
      }));
    } else {
      toast(err, { type: "error" });
    }
    setRevenueLoader(false);
  };

  useEffect(() => {
    fetchCategoriesCount();
    fetchStats();
    fetchTopSellingBrands();
    fetchSaleRecord();
    fetchRevenueRecord();
  }, []);

  useEffect(() => {
    if (saleData?.category === "monthly") {
      const formattedData = allMonths.map(({ month, name }) => {
        const v = saleData?.monthlyData.find((v) => v._id.month === month);
        return {
          name: name,
          // count: v ? v.totalAmount : 0,
          count: v ? v.count : 0,
        };
      });
      setSaleData((prev) => ({ ...prev, data: formattedData }));
    } else if (saleData?.category === "yearly") {
      const formattedData = allYears.map((year) => {
        const v = saleData?.yearlyData.find((v) => v._id.year === year);
        return {
          name: String(year), // year name
          // count: v ? v?.totalAmount : 0,
          count: v ? v?.count : 0,
        };
      });
      setSaleData((prev) => ({ ...prev, data: formattedData }));
    } else {
    }
  }, [saleData?.category, saleData?.totalCount]);

  useEffect(() => {
    if (revenueData?.category === "monthly") {
      const formattedData = allMonths.map(({ month, name }) => {
        const v = revenueData?.monthlyData.find((v) => v._id.month === month);
        return {
          name: name,
          count: v ? v.totalAmount : 0,
        };
      });
      setRevenueData((prev) => ({ ...prev, data: formattedData }));
    } else if (revenueData?.category === "yearly") {
      const formattedData = allYears.map((year) => {
        const v = revenueData?.yearlyData.find((v) => v._id.year === year);
        return {
          name: String(year), // year name
          count: v ? v?.totalAmount : 0,
        };
      });
      setRevenueData((prev) => ({ ...prev, data: formattedData }));
    } else {
    }
  }, [revenueData?.category, revenueData?.totalCount]);

  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 6,
    slidesToScroll: 1,
    arrows: false,
    swipeToSlide: true, // Allows users to drag and swipe freely
    draggable: true,
  };
  return (
    <div className="gap-marging-admin">
      <div className="parent-box">
        <div className="slider-container">
          <Slider {...settings}>
            {statsData.map((item) => (
              <div className="pharmacy-overview-box" key={item.key}>
                <img src={item?.images} alt={item?.label || "-"} />
                <p className="font-Poppins-Medium whitespace-nowrap font-size-20px">
                  {item?.label || "-"}
                </p>
                {statsLoader ? (
                  <CircularProgress size={20} className="theme-color-green" />
                ) : (
                  <p className="font-Poppins-SemiBold font-size-26px theme-color-green">
                    {`${item.key === "totalRevenue" ? CURRENCY : ""}${
                      item.count || "-"
                    }`}
                  </p>
                )}
              </div>
            ))}
          </Slider>
        </div>
      </div>
      <div className="parent-box parent-box-graph-height">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <h2 className="font-Poppins-SemiBold text-[#232323] font-size-20px mb-2">
              Sales
            </h2>
            <div className="user-graph-inner">
              <div className="flex justify-between items-center">
                <div className="font-Poppins-Regular user-graph-header font-size-14px whitespace-nowrap w-auto">
                  Total Sale:{" "}
                  <span className="font-Poppins-SemiBold theme-color-green font-size-20px pl-1">
                    {saleData?.totalCount || 0}
                  </span>
                </div>
                <div className="select-container-graph select-dropdown-container-graps w-auto">
                  <SelectDropdown
                    options={[
                      { label: "Monthly", value: "monthly" },
                      { label: "Yearly", value: "yearly" },
                    ]}
                    isMulti={false}
                    data={[
                      { label: saleData?.category, value: saleData?.category },
                    ]}
                    setData={(e) =>
                      setSaleData((prev) => ({
                        ...prev,
                        category: e[0]?.value,
                      }))
                    }
                    checkboxShow={false}
                    isLabel={false}
                    isRequired={true}
                    clearIcon={false}
                  />
                </div>
              </div>
              {salesLoader && (
                <div className="text-center">
                  <CircularProgress size={20} style={{ color: "#004540" }} />
                </div>
              )}
              <ResponsiveContainer className={"user-graph-container"}>
                <BarChart
                  data={saleData?.data}
                  margin={{ top: 20, right: 0, left: -30, bottom: 0 }}
                  barCategoryGap="15%"
                >
                  <CartesianGrid />
                  <XAxis className="revenue-axis" dataKey="name" />
                  <YAxis className="revenue-axis" />
                  <Tooltip wrapperClassName="revenue-axis" />
                  <Bar dataKey="count" fill="#00443F" radius={[10, 10, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div>
            <h2 className="font-Poppins-SemiBold text-[#232323] font-size-20px mb-2">
              Revenue
            </h2>
            <div className="user-graph-inner">
              <div className="flex justify-between items-center">
                <div className="font-Poppins-Regular user-graph-header-revenue user-graph-header font-size-14px whitespace-nowrap w-auto">
                  Total revenue:{" "}
                  <span className="font-Poppins-SemiBold theme-color-green font-size-20px pl-1">
                    {CURRENCY} {revenueData?.totalCount?.toFixed(2) || 0}
                  </span>
                </div>
                <div className="select-container-graph select-dropdown-container-graps w-auto">
                  <SelectDropdown
                    options={[
                      { label: "Monthly", value: "monthly" },
                      { label: "Yearly", value: "yearly" },
                    ]}
                    isMulti={false}
                    data={[
                      {
                        label: revenueData?.category,
                        value: revenueData?.category,
                      },
                    ]}
                    setData={(e) =>
                      setRevenueData((prev) => ({
                        ...prev,
                        category: e[0]?.value,
                      }))
                    }
                    checkboxShow={false}
                    isLabel={false}
                    isRequired={true}
                    clearIcon={false}
                  />
                </div>
              </div>
              {revenueLoader && (
                <div className="text-center">
                  <CircularProgress size={20} style={{ color: "#004540" }} />
                </div>
              )}
              <ResponsiveContainer className={"user-graph-container"}>
                <LineChart
                  data={revenueData?.data}
                  margin={{ top: 20, right: 0, left: 0, bottom: 0 }}
                >
                  <CartesianGrid />
                  <XAxis className="revenue-axis" dataKey="name" />
                  <YAxis
                    className="revenue-axis"
                    tickFormatter={(value) => `${value}`}
                  />
                  <Tooltip wrapperClassName="revenue-axis" />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#008A80"
                    dot={{ r: 5 }}
                    activeDot={{ r: 7 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <ProgressGraph
            data={brandData?.map((v) => ({
              label: v?.brandName,
              count: v?.totalCount,
            }))}
            totalCount={brandData?.reduce((prev, current) => {
              return prev + current?.totalCount;
            }, 0)}
            loader={brandLoader}
            heading={"Top Selling Brands"}
          />
          <PieChartGraph
            data={pharmaCategories?.categoriesWithCounts?.map((v) => ({
              label: v?.mainCategoryName || "",
              count: v?.productCount || 0,
            }))}
            totalCount={pharmaCategories?.totalProducts || 0}
            loader={categoryLoader}
            circleTitle={"Products"}
            heading={"Pharma Categories"}
          />
        </div>
      </div>
    </div>
  );
};

export default POverView;
